// src/components/SnagModal.jsx

import React, { useState, useEffect } from 'react';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import VoiceRecorder from './VoiceRecorder';

const SnagModal = ({ open, onClose, onSubmit, projectId, teams = [] }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('medium');
  const [category, setCategory] = useState('defect');
  const [submitting, setSubmitting] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState('');
  const [voiceNoteFile, setVoiceNoteFile] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;

    setSubmitting(true);
    try {
      let mediaUrls = [];
      
      // Upload attached files to Firebase Storage if any
      if (attachedFiles.length > 0 || voiceNoteFile) {
        setUploadingFiles(true);
        const storage = getStorage();
        const snagId = `${Date.now()}`; // Use timestamp as snag ID for file naming
        const auth = require('../firebase/firebase').auth;
        const currentUser = auth.currentUser;
        const userId = currentUser?.uid || 'anonymous';
        
        // Upload images
        const imageUploadPromises = attachedFiles.map(async (file, index) => {
          const storageRef = ref(storage, `projects/${projectId}/snags/media/${snagId}_${index}.jpg`);
          await uploadBytes(storageRef, file);
          const url = await getDownloadURL(storageRef);
          return {
            url,
            name: `snag_image_${index}`,
            size: file.size,
            type: 'image',                    // ✅ Simple type (not MIME)
            uploadedAt: new Date(),           // ✅ Audit trail
            uploadedBy: userId                // ✅ Audit trail
          };
        });
        
        mediaUrls = await Promise.all(imageUploadPromises);
        
        // Upload voice note if present
        if (voiceNoteFile) {
          const fileExtension = voiceNoteFile.name.split('.').pop() || 'mp4';
          const voiceStorageRef = ref(storage, `projects/${projectId}/snags/media/${snagId}_voice.${fileExtension}`);
          await uploadBytes(voiceStorageRef, voiceNoteFile);
          const voiceUrl = await getDownloadURL(voiceStorageRef);
          mediaUrls.push({
            url: voiceUrl,
            name: 'snag_voice',
            size: voiceNoteFile.size,
            type: 'voice',                    // ✅ Simple type (not MIME)
            uploadedAt: new Date(),           // ✅ Audit trail
            uploadedBy: userId                // ✅ Audit trail
          });
        }
        
        setUploadingFiles(false);
      }

      await onSubmit({
        title: title.trim(),
        description: description.trim(),
        priority,
        category,
        status: 'open',
        createdAt: new Date().toISOString(),
        media: mediaUrls,
        assignedTeams: selectedTeam ? [selectedTeam] : []
      });
      
      // Reset form
      setTitle('');
      setDescription('');
      setPriority('medium');
      setCategory('defect');
      setAttachedFiles([]);
      setSelectedTeam('');
      setVoiceNoteFile(null);
      onClose();
    } catch (error) {
      console.error('Error creating snag:', error);
    } finally {
      setSubmitting(false);
      setUploadingFiles(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      setTitle('');
      setDescription('');
      setPriority('medium');
      setCategory('defect');
      setAttachedFiles([]);
      setSelectedTeam('');
      setVoiceNoteFile(null);
      onClose();
    }
  };

  const addImageFiles = (files) => {
    const validFiles = Array.from(files || []).filter(f => f.type.startsWith('image/'));
    setAttachedFiles(prev => [...prev, ...validFiles]);
  };

  const handleFileSelect = (e) => {
    addImageFiles(e.target.files);
    e.target.value = '';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!submitting) setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    if (submitting) return;
    addImageFiles(e.dataTransfer.files);
  };

  const removeFile = (index) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== index));
  };

  // Object URLs for image previews - cleanup on unmount
  const [previewUrls, setPreviewUrls] = useState([]);
  useEffect(() => {
    const urls = attachedFiles.map(f => URL.createObjectURL(f));
    setPreviewUrls(urls);
    return () => urls.forEach(u => URL.revokeObjectURL(u));
  }, [attachedFiles]);

  if (!open) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100vw',
      height: '100vh',
      background: 'rgba(0,0,0,0.35)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        background: 'var(--cc-card-bg)',
        borderRadius: 14,
        padding: 0,
        minWidth: 500,
        maxWidth: '90vw',
        maxHeight: '90vh',
        boxShadow: '0 4px 24px rgba(0,0,0,0.12)',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '18px 28px 10px 28px',
          borderBottom: '1px solid var(--branding-app-surface)'
        }}>
          <span style={{ fontWeight: 600, fontSize: 18, color: 'var(--cc-text-primary)' }}>
            Report New Snag
          </span>
          <button
            onClick={handleClose}
            disabled={submitting}
            style={{
              background: 'var(--cc-cancel-bg)',
              color: 'var(--cc-text-primary)',
              border: 'none',
              borderRadius: 6,
              padding: '4px 14px',
              fontWeight: 500,
              cursor: submitting ? 'not-allowed' : 'pointer',
              fontSize: 16,
              marginLeft: 16
            }}
          >
            Close
          </button>
        </div>

        {/* Form Content */}
        <div style={{ padding: 28, overflow: 'auto', maxHeight: 'calc(90vh - 100px)' }}>
          <form onSubmit={handleSubmit} style={{ maxWidth: '600px', margin: '0 auto' }}>
            <div style={{ marginBottom: 20 }}>
              <label style={{
                display: 'block',
                fontWeight: 500,
                color: 'var(--cc-text-primary)',
                marginBottom: 6,
                fontSize: 14
              }}>
                Title *
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                disabled={submitting}
                required
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: 6,
                  fontSize: 14,
                  backgroundColor: submitting ? '#f9fafb' : '#fff',
                  fontFamily: 'inherit',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={{
                display: 'block',
                fontWeight: 500,
                color: 'var(--cc-text-primary)',
                marginBottom: 6,
                fontSize: 14
              }}>
                Description
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={submitting}
                rows={4}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: 6,
                  fontSize: 14,
                  resize: 'vertical',
                  backgroundColor: submitting ? '#f9fafb' : '#fff',
                  fontFamily: 'inherit',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: 16, marginBottom: 20 }}>
              <div style={{ flex: 1 }}>
                <label style={{
                  display: 'block',
                  fontWeight: 500,
                  color: 'var(--cc-text-primary)',
                  marginBottom: 6,
                  fontSize: 14
                }}>
                  Priority
                </label>
                <select
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                  disabled={submitting}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: 6,
                    fontSize: 14,
                    backgroundColor: submitting ? '#f9fafb' : '#fff',
                    boxSizing: 'border-box'
                  }}
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>

              <div style={{ flex: 1 }}>
                <label style={{
                  display: 'block',
                  fontWeight: 500,
                  color: 'var(--cc-text-primary)',
                  marginBottom: 6,
                  fontSize: 14
                }}>
                  Category
                </label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  disabled={submitting}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: 6,
                    fontSize: 14,
                    backgroundColor: submitting ? '#f9fafb' : '#fff',
                    boxSizing: 'border-box'
                  }}
                >
                  <option value="defect">Defect</option>
                  <option value="variation">Variation</option>
                  <option value="safety">Safety Issue</option>
                  <option value="quality">Quality Issue</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>

            {/* Team Assignment Section */}
            {teams.length > 0 && (
              <div style={{ marginBottom: 20 }}>
                <label style={{
                  display: 'block',
                  fontWeight: 500,
                  color: 'var(--cc-text-primary)',
                  marginBottom: 6,
                  fontSize: 14
                }}>
                  Assign to Team
                </label>
                <select
                  value={selectedTeam}
                  onChange={(e) => setSelectedTeam(e.target.value)}
                  disabled={submitting}
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: 6,
                    fontSize: 14,
                    backgroundColor: submitting ? '#f9fafb' : '#fff',
                    boxSizing: 'border-box'
                  }}
                >
                  <option value="">Select a team...</option>
                  {teams.map(team => (
                    <option key={team.id} value={team.id}>
                      {team.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Media Attachment Section */}
            <div style={{ marginBottom: 20 }}>
              <label style={{
                display: 'block',
                fontWeight: 500,
                color: 'var(--cc-text-primary)',
                marginBottom: 6,
                fontSize: 14
              }}>
                Attach Photos
              </label>
              <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                style={{
                  border: `2px dashed ${isDragOver ? 'var(--branding-app-primary)' : '#d1d5db'}`,
                  borderRadius: 6,
                  padding: 20,
                  textAlign: 'center',
                  backgroundColor: isDragOver ? '#eff6ff' : '#fafafa',
                  transition: 'border-color 0.15s, background-color 0.15s'
                }}
              >
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleFileSelect}
                  disabled={submitting}
                  style={{ display: 'none' }}
                  id="file-input"
                />
                <label
                  htmlFor="file-input"
                  style={{
                    display: 'inline-block',
                    background: 'var(--branding-app-primary)',
                    color: '#fff',
                    padding: '8px 16px',
                    borderRadius: 6,
                    cursor: submitting ? 'not-allowed' : 'pointer',
                    fontSize: 14,
                    fontWeight: 500
                  }}
                >
                  Choose photos
                </label>
                <p style={{ margin: '8px 0 0 0', fontSize: 12, color: 'var(--cc-text-muted)' }}>
                  Drag & drop or choose images. Multiple files allowed.
                </p>
              </div>

              {/* Image Preview Thumbnails */}
              {attachedFiles.length > 0 && (
                <div style={{ marginTop: 12 }}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                    {attachedFiles.map((file, index) => (
                      <div
                        key={index}
                        style={{
                          position: 'relative',
                          flexShrink: 0
                        }}
                      >
                        {previewUrls[index] ? (
                          <img
                            src={previewUrls[index]}
                            alt=""
                            style={{
                              width: 64,
                              height: 64,
                              objectFit: 'cover',
                              borderRadius: 6,
                              border: '1px solid var(--branding-app-surface-panel-border)'
                            }}
                          />
                        ) : (
                          <div
                            style={{
                              width: 64,
                              height: 64,
                              borderRadius: 6,
                              border: '1px solid var(--branding-app-surface-panel-border)',
                              background: 'var(--branding-app-surface)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: 24,
                              color: 'var(--branding-app-surface-panel-hint)'
                            }}
                          >
                            🖼️
                          </div>
                        )}
                        <button
                          type="button"
                          onClick={() => removeFile(index)}
                          disabled={submitting}
                          style={{
                            position: 'absolute',
                            top: -6,
                            right: -6,
                            width: 20,
                            height: 20,
                            borderRadius: '50%',
                            background: '#ef4444',
                            color: '#fff',
                            border: 'none',
                            fontSize: 12,
                            fontWeight: 600,
                            cursor: submitting ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: 0,
                            lineHeight: 1,
                            boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                          }}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Voice Note Section */}
            <div style={{ marginBottom: 20 }}>
              <label style={{
                display: 'block',
                fontWeight: 500,
                color: 'var(--cc-text-primary)',
                marginBottom: 6,
                fontSize: 14
              }}>
                Voice Note (Optional)
              </label>
              <VoiceRecorder 
                onVoiceRecorded={(file) => setVoiceNoteFile(file)}
                disabled={submitting}
              />
            </div>

            {/* Form Actions */}
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={handleClose}
                disabled={submitting}
                style={{
                  background: 'var(--branding-app-surface)',
                  color: 'var(--cc-text-secondary)',
                  border: '1px solid var(--branding-app-surface-panel-border)',
                  borderRadius: 6,
                  padding: '10px 20px',
                  fontWeight: 500,
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  fontSize: 14
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting || !title.trim()}
                style={{
                  background: submitting || !title.trim() ? '#9ca3af' : 'var(--branding-app-primary)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 6,
                  padding: '10px 20px',
                  fontWeight: 600,
                  cursor: submitting || !title.trim() ? 'not-allowed' : 'pointer',
                  fontSize: 14
                }}
              >
                {submitting ? (uploadingFiles ? 'Uploading...' : 'Creating...') : 'Create Snag'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SnagModal; 